// @ts-strict-ignore
import {Component, Injectable, NgZone, OnInit} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {
	SimConfigurationService,
	SimHealthMonitorExtendedService,
	SimIdleReloadService,
	NotificationService,
	SimSystemExtendedService,
	SimUserExtendedService,
	SimUtilityService,
	SystemHub,
} from "@services";
import {ConnectionMapping, SystemInformationResponse} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {NavigationEnd, Router} from "@angular/router";
import {fromEvent, interval, merge, Subscription, timer} from "rxjs";
import {Translations} from "@translations";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {tap, throttleTime} from "rxjs/operators";

@UntilDestroy()
@Component({
	selector: "sim-system-actions",
	templateUrl: "./system-actions.component.html",
	styleUrls: ["./system-actions.component.scss"],
	providers: [EventEmitterService],
	standalone: false,
})
export class SystemActionsComponent implements OnInit {
	protected systemInfo: SystemInformationResponse = null;
	protected readonly SimConfigurationService = SimConfigurationService;

	constructor(
		private readonly eventEmitter: EventEmitterService,
		private readonly bonjourService: BonjourService,
		private readonly idleLogoutService: IdleLogoutService,
		private readonly simSystemService: SimSystemExtendedService,
		private readonly simIdleReloadService: SimIdleReloadService,
		private readonly NotificationService: NotificationService,
		private readonly translate: TranslateService,
		private readonly healthMonitorService: SimHealthMonitorExtendedService,
	) {}

	ngOnInit(): void {
		this.simIdleReloadService.activate();
		this.bonjourService.activate();
		this.idleLogoutService.activate();

		this.simSystemService
			.getSystemInformation()
			.then((info) => {
				this.systemInfo = info;
			})
			.finally(() => {
				this.eventEmitter.onSystemHubReconnected.subscribe((_) => {
					this.onConnect();
				});
				this.eventEmitter.onSystemHubConnected.subscribe((_) => {
					this.onConnect();
				});
			});

		this.eventEmitter.onReloadClient.subscribe(() => {
			this.reloadBrowser();
		});

		this.eventEmitter.onRedirectClient.subscribe((data) => {
			window.location.href = data.url;
		});

		this.eventEmitter.onLocalizationChanged.subscribe((data) => {
			if (data.language && data.localizationKey) {
				this.translate.set(data.localizationKey, data.value, data.language);
			} else {
				//TODO whole localization json was imported
			}
		});

		this.eventEmitter.onSystemHubReconnected.subscribe(() => {
			this.NotificationService.notifyInfo(this.translate.instant(Translations.CONNECTION_TO_SYSTEM_REESTABLISHED));
		});

		this.eventEmitter.onSystemHubDisconnected.subscribe(() => {
			this.NotificationService.notifyError(this.translate.instant(Translations.CONNECTION_TO_SYSTEM_LOST));
		});

		this.eventEmitter.onSystemHubError.subscribe(() => {
			this.NotificationService.notifyError(this.translate.instant(Translations.CONNECTION_TO_SYSTEM_LOST));
		});
	}

	private onConnect() {
		this.checkSystemInformation();
		this.healthMonitorService.requestSystemStatusOverWs().then();
	}

	private checkSystemInformation() {
		this.simSystemService.getSystemInformation().then((info) => {
			if (this.systemInfo?.runningToken != info?.runningToken) {
				this.reloadBrowser();
			}
		});
	}

	private reloadBrowser() {
		window.location.reload();
	}
}

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class BonjourService {
	private connectionMapping: ConnectionMapping;

	constructor(
		private eventEmitter: EventEmitterService,
		private systemHub: SystemHub,
		private router: Router,
	) {
		this.eventEmitter.onRevokeMapping.subscribe((data) => {
			if (data.mappingId == this.connectionMapping?.mappingId) {
				this.connectionMapping = null;
			}
		});

		this.eventEmitter.onApproveMapping.subscribe((data) => {
			this.connectionMapping = data.connectionMapping;
		});

		router.events.subscribe(async (event) => {
			if (event instanceof NavigationEnd) {
				let tries = 10;
				while (!(await this.updateConnection(event.urlAfterRedirects)) && tries--) {
					await SimUtilityService.delay(500);
				}
			}
		});
	}

	public activate() {
		interval(5000)
			.pipe(untilDestroyed(this))
			.subscribe(async () => await this.updateConnection(this.router.url));
	}

	private async updateConnection(url: string) {
		if (this.systemHub?.isConnected()) {
			let connectionStatus = {
				currentUrl: url,
				connectionMapping: this.connectionMapping,
			};
			await this.systemHub.updateConnection(connectionStatus);
			return true;
		} else {
			console.debug("UpdateConnection failed - systemHub not connected!");
			return false;
		}
	}
}

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class IdleLogoutService {
	constructor(
		private readonly userService: SimUserExtendedService,
		private readonly zone: NgZone,
	) {
		this.userService.userChangedEmitter.pipe(untilDestroyed(this)).subscribe(() => {
			this.activate();
		});
		SimConfigurationService.configChanged.pipe(untilDestroyed(this)).subscribe(() => {
			if (this.idleLogoutTimeout != SimConfigurationService.settings.idleLogoutTimeout) {
				console.log(`idleLogoutTimeout changed!`);
				this.activate();
			}
		});
		this.activate();
	}

	private userActivitySubscription?: Subscription;
	private inactivityTimerSubscription?: Subscription;
	private idleLogoutTimeout: number;

	activate(): void {
		this.stop();

		this.idleLogoutTimeout = SimConfigurationService.settings.idleLogoutTimeout;
		console.log(`idleLogoutTimeout set to ${this.idleLogoutTimeout}sec`);
		if (this.idleLogoutTimeout <= 0) {
			console.debug("skipping idle logout, because of timeout");
			return;
		}

		const user = this.userService.user;
		if (!user || user?.isAnonymous || user.roleIds.includes("AutoPlayer")) {
			console.debug("skipping idle logout, because of user");
			return;
		}

		this.zone.runOutsideAngular(() => {
			this.userActivitySubscription = merge(
				fromEvent(window, "mousemove"),
				fromEvent(window, "keydown"),
				fromEvent(window, "click"),
				fromEvent(window, "scroll"),
				fromEvent(window, "touchstart"),
			)
				.pipe(
					untilDestroyed(this),
					throttleTime(10000),
					tap(() => this.resetTimer()),
				)
				.subscribe();
		});

		this.resetTimer();
	}

	private resetTimer(): void {
		console.debug("reset...");

		if (this.inactivityTimerSubscription) {
			this.inactivityTimerSubscription.unsubscribe();
		}

		this.inactivityTimerSubscription = timer(this.idleLogoutTimeout * 1000)
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.zone.run(() => {
					this.onInactivity();
				});
			});
	}

	private onInactivity() {
		this.userService.logout().then();
		this.stop();
	}

	private stop() {
		if (this.inactivityTimerSubscription) {
			this.inactivityTimerSubscription.unsubscribe();
		}
		if (this.userActivitySubscription) {
			this.userActivitySubscription.unsubscribe();
		}
	}
}
